import { PrefectureConfig } from './server';
import { getToken } from '@/utils/cookie';
import { getLocalStorage } from '@/utils/localStorage';

export interface PrefectureItem {
  id: number;
  name: string;
  value: number;
  purePC?: boolean;
  web?: boolean;
  label?: string;
}

export const getPowerOfTwo = (n: number): number => 1 << n;

const RJ_CHANNEL_CODES = ['202706', '10001', '4227'] as const;
// 热江专属渠道号段：80000-89999
const RJ_CHANNEL_RANGE_MIN = 80000;
const RJ_CHANNEL_RANGE_MAX = 89999;

const isRJChannelRange = (channel: string) => {
  const channelNumber = Number(channel);
  return channelNumber >= RJ_CHANNEL_RANGE_MIN && channelNumber <= RJ_CHANNEL_RANGE_MAX;
};

// 热江渠道包：channel 包含任一热江渠道码
export const isRJChannelPackage = () => {
  const channel = getLocalStorage('channel');
  return !!channel && (RJ_CHANNEL_CODES.some(code => channel.includes(code)) || isRJChannelRange(channel));
};

// 获取初始化的 NAV_TABBTN_ID：热江渠道包时返回 RJ_ID，否则返回默认值
export const getInitialNavTabId = (defaultId: number): number => {
  if (isRJChannelPackage()) {
    return RJ_ID;
  }
  return defaultId;
};

export const LEGEND_ID = 1;
export const LEGENDARY_ID = 2;
export const LEGEND_THREE_ID = 3;
export const LEGEND_PC_ONLY_ID = 5;
export const PLAYFUL_ID = 6;
export const WEBGAME_ID = 19;
export const LIANGSHAN_ID = getPowerOfTwo(6);
export const RJ_ID = 7;
export const HUAJIU_ID = 8;

const FLAG_LEGEND = getPowerOfTwo(0);
const FLAG_LEGENDARY = getPowerOfTwo(1);
const FLAG_LEGEND_THREE = getPowerOfTwo(2);
const FLAG_LEGEND_PC_ONLY = getPowerOfTwo(3);
const FLAG_PLAYFUL = getPowerOfTwo(4);
const FLAG_WEBGAME = getPowerOfTwo(5);
const FLAG_LIANGSHAN_LEGEND = getPowerOfTwo(6);
const FLAG_RJ = getPowerOfTwo(7);
const FLAG_HUAJIU = getPowerOfTwo(8);

// 普通渠道包的导航列表（默认顺序）
export const prefectures: PrefectureItem[] = [
  {
    id: LEGEND_ID,
    name: '传奇专区',
    value: FLAG_LEGEND,
    label: '传奇'
  },
  {
    id: LEGENDARY_ID,
    name: '传世专区',
    value: FLAG_LEGENDARY,
    label: '传世'
  },
  {
    id: LEGEND_THREE_ID,
    name: '传奇3专区',
    value: FLAG_LEGEND_THREE,
    label: '传三'
  },
  {
    id: LEGEND_PC_ONLY_ID,
    name: '纯PC专区',
    value: FLAG_LEGEND_PC_ONLY,
    // purePC: true
    label: '纯PC'
  },
  {
    id: HUAJIU_ID,
    name: '怀旧专区',
    value: FLAG_HUAJIU,
    label: '怀旧'
  },
  {
    id: RJ_ID,
    name: '热江专区',
    value: FLAG_RJ,
    label: '热血江湖'
  },
  {
    id: PLAYFUL_ID,
    name: '明星专区',
    value: FLAG_PLAYFUL
  },
  {
    id: WEBGAME_ID,
    name: '冰雪之战',
    value: FLAG_WEBGAME,
    web: true
  },
  {
    id: LIANGSHAN_ID,
    name: '品牌专区',
    value: FLAG_LIANGSHAN_LEGEND,
    web: true
  }
];

// 热江渠道包的导航列表（热江专区排在第一位）
export const prefecturesForRJ: PrefectureItem[] = [
  {
    id: RJ_ID,
    name: '热江专区',
    value: FLAG_RJ
  },
  {
    id: LEGEND_ID,
    name: '传奇专区',
    value: FLAG_LEGEND
  },
  {
    id: LEGENDARY_ID,
    name: '传世专区',
    value: FLAG_LEGENDARY
  },
  {
    id: LEGEND_THREE_ID,
    name: '传奇3专区',
    value: FLAG_LEGEND_THREE
  },
  {
    id: LEGEND_PC_ONLY_ID,
    name: '纯PC专区',
    value: FLAG_LEGEND_PC_ONLY
    // purePC: true
  },
  {
    id: HUAJIU_ID,
    name: '怀旧专区',
    value: FLAG_HUAJIU
  },
  {
    id: PLAYFUL_ID,
    name: '明星专区',
    value: FLAG_PLAYFUL
  },
  {
    id: WEBGAME_ID,
    name: '冰雪之战',
    value: FLAG_WEBGAME,
    web: true
  },
  {
    id: LIANGSHAN_ID,
    name: '品牌专区',
    value: FLAG_LIANGSHAN_LEGEND,
    web: true
  }
];

const DEFAULT_FLAGS = '11';

export function getPrefectureList(binaryStr = DEFAULT_FLAGS): PrefectureItem[] {
  const isValidBinary = /^(0|1)+$/.test(binaryStr);
  const binaryToUse = isValidBinary ? binaryStr : DEFAULT_FLAGS;
  const value = parseInt(binaryToUse, 2);

  // 根据渠道包类型选择不同的基础列表
  const baseList = isRJChannelPackage() ? prefecturesForRJ : prefectures;
  const result = baseList.filter(prefecture => (prefecture.value & value) !== 0);

  if (
    (process.env.NODE_ENV === 'development' || localStorage.getItem('HUAJIU_PREFECTURE_DEV') === '1') &&
    !result.some(prefecture => prefecture.id === HUAJIU_ID)
  ) {
    const huaijiuPrefecture = baseList.find(prefecture => prefecture.id === HUAJIU_ID);
    if (huaijiuPrefecture) result.push(huaijiuPrefecture);
  }

  return result;
}

export function getBinaryStr(config?: PrefectureConfig): string {
  let flags = FLAG_LEGEND | FLAG_LEGENDARY;
  const forceRJ = isRJChannelPackage();

  if (config) {
    const { isShow, isShowPurePcSwitch, isShowTanWanSwitch, isZoneGameUrl, lsGameH5Url, enableIndependenceRexueGame } =
      config;
    if (isShow) flags |= FLAG_LEGEND_THREE;
    if (isShowPurePcSwitch) flags |= FLAG_LEGEND_PC_ONLY;
    if (isShowTanWanSwitch) flags |= FLAG_PLAYFUL;
    if (isZoneGameUrl) flags |= FLAG_WEBGAME;
    if (lsGameH5Url) flags |= FLAG_LIANGSHAN_LEGEND;
    if (enableIndependenceRexueGame) flags |= FLAG_RJ;
  }

  // 热江渠道包不受后台开关控制，强制开启热江专区
  if (forceRJ) flags |= FLAG_RJ;

  // 本地联调用：后续接后台开关后移除开发环境强制展示。
  if (process.env.NODE_ENV === 'development' || localStorage.getItem('HUAJIU_PREFECTURE_DEV') === '1') {
    flags |= FLAG_HUAJIU;
  }

  return flags.toString(2);
}

export const isPlayfulPrefecture = () => {
  const localId = localStorage.getItem('NAV_TABBTN_ID');
  const oldId = localId ? Number(localId) : 1;
  return PLAYFUL_ID === oldId;
};

export const isWebGamePrefecture = () => {
  const localId = localStorage.getItem('NAV_TABBTN_ID');
  const oldId = localId ? Number(localId) : 1;
  const ids = prefectures.filter(prefecture => prefecture.web).map(prefecture => prefecture.id);

  if (ids.includes(oldId)) return true;
  return false;
};

// 是否是热江专区
export const isRJPrefecture = () => {
  const localId = localStorage.getItem('NAV_TABBTN_ID');
  const oldId = localId ? Number(localId) : 1;
  return RJ_ID === oldId;
};

export const isHuaijiuPrefecture = () => {
  const localId = localStorage.getItem('NAV_TABBTN_ID');
  const oldId = localId ? Number(localId) : 1;
  return HUAJIU_ID === oldId;
};

export function setWebIframeUrls(data?: PrefectureConfig) {
  let url = '';

  if (data?.isZoneGameUrl) {
    url += `start${WEBGAME_ID}${data?.isZoneGameUrl}end${WEBGAME_ID}`;
  }
  if (data?.lsGameH5Url) {
    url += `start${LIANGSHAN_ID}${data?.lsGameH5Url}end${LIANGSHAN_ID}`;
  }

  localStorage.setItem('PREFECTURE_WEBGAME_URLS', url);
}

function getWebAppv() {
  return localStorage.getItem('appv')?.replace(/&.+/, '') ?? '';
}

function appendParams(url: string) {
  const hasQuery = url.includes('?');
  const paramsStr = `device=pc&appv=${getWebAppv()}&token=${getToken()}`;
  return hasQuery ? `${url}&${paramsStr}` : `${url}?${paramsStr}`;
}

export function getWebIframeUrl() {
  const url = localStorage.getItem('PREFECTURE_WEBGAME_URLS');
  if (!url) return '';

  const localId = localStorage.getItem('NAV_TABBTN_ID');
  if (!localId) return '';

  const start = `start${localId}`;
  const end = `end${localId}`;
  const startIndex = url.indexOf(start);
  const endIndex = url.indexOf(end);

  if (startIndex === -1 || endIndex === -1) return '';

  if (startIndex < endIndex) {
    const result = url.substring(startIndex + start.length, endIndex);
    return appendParams(result);
  }

  return '';
}
